import React, { FC, Suspense, useMemo } from 'react';
import classnames from 'classnames';
import { ErrorBoundary, useRouter, useSignal } from '@funtech-int/rival-react';
import {
    PaymentPlace,
    TarifficatorPurchaseSuccessWidgetEvent,
    useAnalytics,
    usePaymentPlace,
} from '@funtech-int/yango-shared';

import { Button } from '@/shared/components/Button/Button';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { useI18n } from '@/app/hooks/useI18n';
import { createHeaderOfferAnalytics } from '@/app/lib/headerOfferAnalytics';
import { mainRoute } from '@/scenarios/home';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { usePaymentWidget } from '@/shared/hooks/usePaymentWidget';
import { usePaymentIntegrations } from '@/shared/hooks/usePaymentIntegrations';

import styles from './HeaderSubscriptionButton.module.css';

interface Props {
    className: string;
}

interface HeaderSubscriptionInternalProps extends Props {
    paymentPlace: PaymentPlace;
}

const HeaderSubscriptionComponent: FC<HeaderSubscriptionInternalProps> = ({
    className,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const router = useRouter();
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const offer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.Header,
    });

    const offerAnalytics = useAnalytics(createHeaderOfferAnalytics);
    const buttonText = i18n('header__subscription_button_title');

    const { purchaseSucceeded: sendSuccessPurchaseEvent } = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidget();
    const paymentIntegrations = usePaymentIntegrations();

    if (offer) {
        offerAnalytics.setData({ offer, buttonText });
    }

    const controller = useMemo(() => {
        async function onPurchaseSuccess(event: TarifficatorPurchaseSuccessWidgetEvent) {
            await paymentIntegrations.submit(event);
            subscriptionRefetch();
            sendSuccessPurchaseEvent(event);
            offerAnalytics.succeeded();

            router.navigate({ to: mainRoute() });
        }
        return {
            onOfferShow: () => {
                if (!offer) return;
                offerAnalytics.showed();
                paymentPlace.analytics.offerShowed(offer);
            },
            onOfferClick: () => {
                if (!offer) return;
                offerAnalytics.navigated();
                paymentPlace.analytics.offerClicked(offer);

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                });
            },
        };
    }, [
        subscriptionRefetch,
        sendSuccessPurchaseEvent,
        offer,
        paymentWidget,
        paymentIntegrations,
        router,
        offerAnalytics,
        paymentPlace,
    ]);

    if (!offer) {
        return null;
    }

    return (
        <Button
            className={classnames(className, styles.button)}
            size={32}
            theme="dark"
            variant="plus"
            onClick={controller.onOfferClick}
            onShow={controller.onOfferShow}
        >
            {buttonText}
        </Button>
    );
};

const HeaderSubscriptionPurchaseContainer = (props: Props) => {
    const page = usePaymentPage();

    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.Header],
    });

    if (!paymentPlace) {
        return null;
    }

    return <HeaderSubscriptionComponent {...props} paymentPlace={paymentPlace} />;
};

export const HeaderSubscriptionButton = (props: Props) => {
    const profileManager = useProfileManager();

    const hasSubscription = useSignal(profileManager.hasSubscription);

    if (hasSubscription) {
        return null;
    }

    return (
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <HeaderSubscriptionPurchaseContainer {...props} />
            </Suspense>
        </ErrorBoundary>
    );
};
